import md5 from 'md5';
import CONFIG from "../../config";
import Store from "../store/Store";
import { TAnswer, TError, TUser, TTask, TTaskDetail, TSubmissionResponse, TAIReview, TAISettings, TChatMessage, TChatSendResponse, TChatConfigResponse, TUserStatistics, TAchievement, TUnlockedAchievement, TArticle, TArticleDetail, TArticleLikeResponse, TArticleComment, TAIModel, TProject, TProjectDetail, TProjectFile } from "./types";

const { HOST } = CONFIG;

class Server {
    HOST = HOST;
    store: Store;
    showErrorCb: (error: TError) => void = () => {};

    constructor(store: Store) {
        this.store = store;
    }

    // посылает запрос и обрабатывает ответ
    private async request<T>(method: string, params: { [key: string]: string } = {}): Promise<T | null> {
        try {
            params.method = method;
            const token = this.store.getToken();
            if (token) {
                params.token = token;
            }
            const response = await fetch(`${this.HOST}/?${Object.keys(params).map(key => `${key}=${encodeURIComponent(params[key])}`).join('&')}`);
            const answer: TAnswer<T> = await response.json();
            if (answer.result === 'ok' && answer.data) {
                return answer.data;
            }
            answer.error && this.setError(answer.error);
            return null;
        } catch (e) {
            console.log(e);
            this.setError({
                code: 9000,
                text: 'Unknown error',
            });
            return null;
        }
    }

    private lastError: TError | null = null;

    private setError(error: TError): void {
        this.lastError = error;
        this.showErrorCb(error);
    }

    getError(): TError | null {
        return this.lastError;
    }

    showError(cb: (error: TError) => void) {
        this.showErrorCb = cb;
    }

    async login(email: string, password: string): Promise<boolean> {
        const rnd = Math.round(Math.random() * 100000);
        const passHash = md5(password);
        const hash = md5(`${passHash}${rnd}`);  
        const user = await this.request<TUser>('login', { email, hash, rnd: `${rnd}` });
        if (user) {
            this.store.setUser(user);
            return true;
        }
        return false;
    }

    async logout() {
        const result = await this.request<boolean>('logout');
        if (result) {
            this.store.clearUser();
        }
    }

    /**
     * Проверить валидность текущей сессии
     * Возвращает данные пользователя если токен валиден, иначе null
     * Также возвращает null при ошибке авторизации (705), но не при других ошибках
     */
    async checkSession(): Promise<{ user: TUser | null; isAuthError: boolean }> {
        const token = this.store.getToken();
        if (!token) {
            return { user: null, isAuthError: true };
        }
        try {
            const params: { [key: string]: string } = {};
            params.method = 'getCurrentUser';
            params.token = token;
            const response = await fetch(`${this.HOST}/?${Object.keys(params).map(key => `${key}=${params[key]}`).join('&')}`);
            const answer: TAnswer<TUser> = await response.json();
            if (answer.result === 'ok' && answer.data) {
                return { user: answer.data, isAuthError: false };
            }
            // Если ошибка авторизации (705), это реальная проблема с токеном
            if (answer.error && answer.error.code === 705) {
                return { user: null, isAuthError: true };
            }
            // При других ошибках не считаем это проблемой авторизации
            // (это может быть временная ошибка сети или сервера)
            return { user: null, isAuthError: false };
        } catch (e) {
            console.log('checkSession error:', e);
            // При ошибке сети не считаем это проблемой авторизации
            return { user: null, isAuthError: false };
        }
    }
    // async logout(): Promise<void> {
    //     const result = await this.request<boolean>('logout');
    //     if (result) {
    //         this.store.clearUser();
    //     }
    // }
    async updateUserName(newName: string): Promise<boolean> {
        // Вызываем серверный метод updateUserName, передавая новое имя
        const result = await this.request<boolean>('updateUserName', { newName });
        
        if (result) {
            // Если сервер вернул 'ok', обновляем имя локально в Store
            this.store.setUserName(newName); 
            return true;
        }
        return false;
    }


    async registration(email: string, password: string, name: string): Promise<boolean> {
        const passHash = md5(password);
        // 1. Ожидаем от сервера полный объект пользователя (TUser)
        const user = await this.request<TUser>('registration', { email, password: passHash, name });

        // 2. Если пользователь успешно создан и получен...
        if (user) {
            // 3. ...сохраняем его данные в store, чтобы он сразу вошел в систему
            this.store.setUser(user);
            // 4. Устанавливаем модель по умолчанию для нового пользователя
            this.store.setAIModel('nvidia/nemotron-3-nano-30b-a3b:free');
            return true;
        }
        // 5. Если что-то пошло не так, возвращаем false
        return false;
    }

    async getTasks(difficulty?: string, languageId?: number): Promise<TTask[] | null> {
        const params: { [key: string]: string } = {};
        if (difficulty && difficulty !== 'all') {
            params.difficulty = difficulty;
        }
        if (languageId) {
            params.language_id = languageId.toString();
        }
        return await this.request<TTask[]>('getTasks', params);
    }
    
    async getLanguages(): Promise<any[] | null> {
        return await this.request<any[]>('getLanguages', {});
    }

    async getTaskById(taskId: number): Promise<TTaskDetail | null> {
        return await this.request<TTaskDetail>('getTaskById', { task_id: taskId.toString() });
    }

    async getArticles(): Promise<TArticle[] | null> {
        return await this.request<TArticle[]>('getArticles', {});
    }

    async getArticleById(articleRef: number | string): Promise<TArticleDetail | null> {
        return await this.request<TArticleDetail>('getArticleById', { article_id: articleRef.toString() });
    }

    async toggleArticleLike(articleId: number): Promise<TArticleLikeResponse | null> {
        return await this.request<TArticleLikeResponse>('toggleArticleLike', { article_id: articleId.toString() });
    }

    async getArticleComments(articleId: number): Promise<TArticleComment[] | null> {
        return await this.request<TArticleComment[]>('getArticleComments', { article_id: articleId.toString() });
    }

    async addArticleComment(articleId: number, content: string): Promise<TArticleComment | null> {
        try {
            const body: { [key: string]: unknown } = { article_id: articleId, content };
            const token = this.store.getToken();
            if (token) body.token = token;
            const response = await fetch(`${this.HOST}/?method=addArticleComment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const answer: TAnswer<TArticleComment> = await response.json();
            if (answer.result === 'ok' && answer.data) return answer.data;
            if (answer.error) this.setError(answer.error);
            return null;
        } catch (e) {
            this.setError({ code: 9000, text: 'Unknown error' });
            return null;
        }
    }

    async getProjects(difficulty?: string, languageId?: number): Promise<TProject[] | null> {
        const params: { [key: string]: string } = {};
        if (difficulty && difficulty !== 'all') {
            params.difficulty = difficulty;
        }
        if (languageId) {
            params.language_id = languageId.toString();
        }
        return await this.request<TProject[]>('getProjects', params);
    }

    async getProjectById(projectId: number): Promise<TProjectDetail | null> {
        return await this.request<TProjectDetail>('getProjectById', { project_id: projectId.toString() });
    }

    async getProjectFiles(projectId: number): Promise<TProjectFile[] | null> {
        return await this.request<TProjectFile[]>('getProjectFiles', { project_id: projectId.toString() });
    }

    async submitTask(
        taskId: number, 
        userDescription: string,
        chatHistory?: TChatMessage[]
    ): Promise<TSubmissionResponse | null> {
        try {
            // Используем POST для передачи больших данных (история чата)
            // method передаем в URL, остальные данные в теле запроса
            const body: { [key: string]: any } = {
                task_id: taskId.toString(),
                user_description: userDescription,
                ai_model: this.store.getAIModel()
            };
            
            const token = this.store.getToken();
            if (token) {
                body.token = token;
            } else {
                console.warn('[Server.ts] WARNING: No token found in store!');
            }
            
            // Добавляем историю чата, если она есть
            if (chatHistory && chatHistory.length > 0) {
                // Преобразуем TChatMessage[] в формат для сервера
                body.chat_history = chatHistory.map(msg => ({
                    type: msg.type,
                    content: msg.content,
                    timestamp: msg.timestamp,
                    score: msg.review?.score,
                    feedback: msg.review?.feedback
                }));
            }
            
            // Отправляем POST запрос с method в URL для обратной совместимости
            // и данными в теле запроса
            const response = await fetch(`${this.HOST}/?method=submitTask`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body)
            });
            
            const answer: TAnswer<TSubmissionResponse> = await response.json();            
            if (answer.result === 'ok' && answer.data) {
                // Проверяем наличие обязательных полей
                if (typeof answer.data.submission_id === 'number' && 
                    typeof answer.data.score === 'number' && 
                    typeof answer.data.feedback === 'string') {
                    return answer.data;
                } else {
                    console.error('Invalid response data structure:', answer.data);
                    this.setError({
                        code: 9003,
                        text: 'AI Service returned invalid data structure',
                    });
                    const error = new Error('AI_PARSE_ERROR') as Error & { errorCode: number };
                    error.errorCode = 9003;
                    throw error;
                }
            }
            if (answer.error) {
                // Если ошибка 9003, выбрасываем специальное исключение
                if (answer.error.code === 9003) {
                    const error = new Error('AI_PARSE_ERROR') as Error & { errorCode: number };
                    error.errorCode = 9003;
                    throw error;
                }
                this.setError(answer.error);
            }
            return null;
        } catch (e) {
            // Если это наше специальное исключение, пробрасываем дальше
            if (e instanceof Error && 'errorCode' in e && (e as any).errorCode === 9003) {
                throw e;
            }
            console.error('submitTask error:', e);
            this.setError({
                code: 9000,
                text: 'Unknown error',
            });
            return null;
        }
    }

    async getSubmissionReview(submissionId: number): Promise<TAIReview | null> {
        return await this.request<TAIReview>('getSubmissionReview', { 
            submission_id: submissionId.toString() 
        });
    }

    /**
     * Получить настройки AI пользователя
     */
    async getAISettings(): Promise<TAISettings | null> {
        return await this.request<TAISettings>('getAISettings');
    }

    /**
     * Обновить настройки AI пользователя
     */
    async updateAISettings(settings: TAISettings): Promise<boolean> {
        const params: { [key: string]: string } = {};
        if (settings.ai_model) {
            params.ai_model = settings.ai_model;
        }
        
        const result = await this.request<boolean>('updateAISettings', params);
        return result !== null;
    }

    /**
     * Получить список доступных валидированных AI моделей
     */
    async getAvailableModels(): Promise<TAIModel[] | null> {
        return await this.request<TAIModel[]>('getAvailableModels');
    }

    async getUserStatistics(): Promise<TUserStatistics | null> {
        return await this.request<TUserStatistics>('getUserStatistics');
    }

    // ========== МЕТОДЫ ДЛЯ РАБОТЫ С ДОСТИЖЕНИЯМИ ==========

    /**
     * Получить все достижения с прогрессом для текущего пользователя
     */
    async getUserAchievements(): Promise<TAchievement[] | null> {
        return await this.request<TAchievement[]>('getUserAchievements');
    }

    /**
     * Получить непросмотренные достижения (для уведомлений)
     */
    async getUnnotifiedAchievements(): Promise<TUnlockedAchievement[] | null> {
        return await this.request<TUnlockedAchievement[]>('getUnnotifiedAchievements');
    }

    /**
     * Отметить достижение как просмотренное
     */
    async markAchievementNotified(achievementId: number): Promise<boolean> {
        const result = await this.request<boolean>('markAchievementNotified', {
            achievement_id: achievementId.toString()
        });
        return result !== null;
    }

    /**
     * Отметить все достижения как просмотренные
     * (Зарезервировано для будущей функции "Отметить все как прочитанное")
     */
    // async markAllAchievementsNotified(): Promise<boolean> {
    //     const result = await this.request<boolean>('markAllAchievementsNotified');
    //     return result !== null;
    // }

    /**
     * Отправить проект на проверку AI
     */
    async submitProject(
        projectId: number,
        userMessage: string,
        chatHistory?: TChatMessage[]
    ): Promise<TSubmissionResponse | null> {
        try {
            const body: { [key: string]: any } = {
                project_id: projectId.toString(),
                user_message: userMessage,
                ai_model: this.store.getAIModel()
            };
            
            const token = this.store.getToken();
            if (token) {
                body.token = token;
            } else {
                console.warn('[Server.ts] WARNING: No token found in store!');
            }
            
            // Добавляем историю чата, если она есть
            if (chatHistory && chatHistory.length > 0) {
                body.chat_history = chatHistory.map(msg => ({
                    type: msg.type,
                    content: msg.content,
                    timestamp: msg.timestamp,
                    score: msg.review?.score,
                    feedback: msg.review?.feedback
                }));
            }
            
            const response = await fetch(`${this.HOST}/?method=submitProject`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body)
            });
            
            const answer: TAnswer<TSubmissionResponse> = await response.json();
            if (answer.result === 'ok' && answer.data) {
                if (typeof answer.data.submission_id === 'number' && 
                    typeof answer.data.score === 'number' && 
                    typeof answer.data.feedback === 'string') {
                    return answer.data;
                } else {
                    console.error('Invalid response data structure:', answer.data);
                    this.setError({
                        code: 9003,
                        text: 'AI Service returned invalid data structure',
                    });
                    const error = new Error('AI_PARSE_ERROR') as Error & { errorCode: number };
                    error.errorCode = 9003;
                    throw error;
                }
            }
            if (answer.error) {
                if (answer.error.code === 9003) {
                    const error = new Error('AI_PARSE_ERROR') as Error & { errorCode: number };
                    error.errorCode = 9003;
                    throw error;
                }
                this.setError(answer.error);
            }
            return null;
        } catch (e) {
            if (e instanceof Error && 'errorCode' in e && (e as any).errorCode === 9003) {
                throw e;
            }
            this.setError({
                code: 9000,
                text: 'Unknown error',
            });
            return null;
        }
    }

    /**
     * Сообщение в чат (OpenRouter через сервер). Без токена — гость: на сервере модель по умолчанию (см. ChatController).
     */
    async getChatConfig(): Promise<TChatConfigResponse | null> {
        return await this.request<TChatConfigResponse>('getChatConfig');
    }

    /**
     * Сообщение в чат (OpenRouter через сервер). Без токена — гость: на сервере модель по умолчанию (см. ChatController).
     */
    async sendChatMessage(
        message: string,
        history: { role: 'user' | 'assistant'; content: string }[],
        aiModel: string,
        chatConfig: { role: string; detail: 'brief' | 'detailed' }
    ): Promise<TChatSendResponse | null> {
        try {
            const body: { [key: string]: unknown } = {
                message,
                history,
                ai_model: aiModel,
                chat_role: chatConfig.role,
                chat_detail: chatConfig.detail,
            };
            const token = this.store.getToken();
            if (token) {
                body.token = token;
            }

            const response = await fetch(`${this.HOST}/?method=sendChatMessage`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });

            const answer: TAnswer<TChatSendResponse> = await response.json();
            if (answer.result === 'ok' && answer.data && typeof answer.data.reply === 'string') {
                return answer.data;
            }
            if (answer.error) {
                this.setError(answer.error);
            }
            return null;
        } catch (e) {
            console.error('sendChatMessage error:', e);
            this.setError({
                code: 9000,
                text: 'Unknown error',
            });
            return null;
        }
    }
} 

export default Server;